import { DialogContent, Stack } from "@mui/material";
import { logEvent } from "@src/appV2/lib/analytics";
import {
  BottomSheet,
  CounterText,
  Divider,
  PageHeaderWrapper,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useMemo } from "react";
import { Controller } from "react-hook-form";

import { DistanceFilter } from "../../../ShiftDiscovery/Filters/BottomSheet/DistanceFilter";
import { FiltersBottomSheetFooter } from "../../../ShiftDiscovery/Filters/BottomSheet/Footer";
import { IN_HOME_USER_EVENTS } from "../../lib/events";
import { type VisitFilters } from "../types";
import { useVisitFiltersForm } from "../useVisitFiltersForm";
import { PayFilter } from "./PayFilter";
import { CareTypeFilter, ShiftRolesFilter, VisitTypeFilter } from "./ToggleFilters";
import { VisitsPerWeekFilter } from "./VisitsPerWeekFilter";

interface VisitFiltersBottomSheetProps {
  modalState: UseModalState;
  getFilteredVisitsCount: (filters: Partial<VisitFilters>) => number;
  datasetPayMin: number;
}

export function VisitFiltersBottomSheet(props: VisitFiltersBottomSheetProps) {
  const { modalState, getFilteredVisitsCount, datasetPayMin } = props;

  const { form, formValues, changedFiltersCount, resetFilters, cancelFilters, submitFilters } =
    useVisitFiltersForm();

  const filteredVisitsCount = useMemo(
    () => getFilteredVisitsCount(formValues),
    [getFilteredVisitsCount, formValues]
  );

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <FiltersBottomSheetFooter
          showClearFormButton={changedFiltersCount > 0}
          openShiftsCount={filteredVisitsCount}
          openShiftsIsLoading={false}
          entityLabel="visits"
          onClear={() => {
            logEvent(IN_HOME_USER_EVENTS.FILTERS_CLEARED);
            resetFilters();
          }}
          onCancel={() => {
            logEvent(IN_HOME_USER_EVENTS.FILTERS_CLOSED);
            cancelFilters();
            modalState.closeModal();
          }}
          onSubmit={() => {
            logEvent(IN_HOME_USER_EVENTS.FILTERS_APPLIED, {
              shiftRoles: formValues.shiftRoles,
              excludedCareTypes: formValues.excludedCareTypes,
              visitTypes: formValues.visitTypes,
              visitsPerWeekMaximum: formValues.visitsPerWeekMaximum,
              payMinimum: formValues.payMinimum,
              distance: formValues.distance,
              filteredVisitsCount,
            });
            submitFilters();
            modalState.closeModal();
          }}
        />
      }
    >
      <DialogContent sx={{ paddingBottom: 12, overflowX: "hidden", overscrollBehavior: "contain" }}>
        <Stack spacing={8}>
          <PageHeaderWrapper variant="tertiary">
            <CounterText count={changedFiltersCount} suffix="applied">
              <Title variant="h4" component="h4">
                Filters
              </Title>
            </CounterText>
          </PageHeaderWrapper>

          <Controller
            control={form.control}
            name="shiftRoles"
            render={({ field }) => (
              <ShiftRolesFilter value={field.value} onChange={field.onChange} />
            )}
          />

          <Divider />

          <Controller
            control={form.control}
            name="distance"
            render={({ field }) => <DistanceFilter value={field.value} onChange={field.onChange} />}
          />

          <Divider />

          <Controller
            control={form.control}
            name="excludedCareTypes"
            render={({ field }) => <CareTypeFilter value={field.value} onChange={field.onChange} />}
          />

          <Divider />

          <Controller
            control={form.control}
            name="visitTypes"
            render={({ field }) => (
              <VisitTypeFilter value={field.value} onChange={field.onChange} />
            )}
          />

          <Divider />

          <Controller
            control={form.control}
            name="visitsPerWeekMaximum"
            render={({ field }) => (
              <VisitsPerWeekFilter value={field.value} onChange={field.onChange} />
            )}
          />

          <Divider />

          <Controller
            control={form.control}
            name="payMinimum"
            render={({ field }) => (
              <PayFilter value={field.value} minCents={datasetPayMin} onChange={field.onChange} />
            )}
          />
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
